import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Plus, Edit2, Trash2, CheckCircle, Clock, DollarSign, AlertTriangle } from 'lucide-react';
import { DealTask } from '@/hooks/useBrandDeals';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface BrandDealTaskManagerProps {
  dealId: string;
  tasks: DealTask[];
  onCreateTask: (dealId: string, taskData: Partial<DealTask>) => Promise<void>;
  onUpdateTask: (taskId: string, updates: Partial<DealTask>) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
}

export const BrandDealTaskManager: React.FC<BrandDealTaskManagerProps> = ({
  dealId,
  tasks,
  onCreateTask,
  onUpdateTask,
  onDeleteTask
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    status: string;
    progress_percentage: string;
    payment_amount: string;
  } | null>(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    start_date: '',
    due_date: '',
    assigned_to: 'brand',
    status: 'pending',
    payment_amount: 0,
  });
  const [fixedTaskIds, setFixedTaskIds] = useState<Set<string>>(new Set());

  // Auto-fix any completed tasks with payment amounts but incorrect payment_status
  useEffect(() => {
    const fixInconsistentPaymentStatus = async () => {
      const tasksToFix = tasks.filter(t => 
        t.status === 'completed' && 
        t.payment_amount && 
        t.payment_amount > 0 && 
        t.payment_status === 'unpaid' &&
        !fixedTaskIds.has(t.id)
      );

      if (tasksToFix.length > 0) {
        console.log('[BrandDealTaskManager] Normalizing payment_status to "due" for:', tasksToFix.map(t => ({ id: t.id, title: t.title })));
      }
      
      for (const task of tasksToFix) {
        try {
          await onUpdateTask(task.id, { payment_status: 'due' });
          setFixedTaskIds(prev => new Set([...prev, task.id]));
          console.log('[BrandDealTaskManager] ✓ Fixed payment status for task', task.id);
        } catch (error) {
          console.error('[BrandDealTaskManager] Failed to fix payment status for task:', task.id, error);
        }
      }
    };
    
    if (tasks.length > 0) {
      fixInconsistentPaymentStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    try {
      await onCreateTask(dealId, newTask);
      setNewTask({ title: '', description: '', start_date: '', due_date: '', assigned_to: 'brand', status: 'pending', payment_amount: 0 });
      setIsCreating(false);
      toast.success('Task created successfully');
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handlePayTask = async (task: DealTask) => {
    if (!confirm(`Confirm payment of $${task.payment_amount?.toLocaleString()} for "${task.title}"?`)) {
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('pay-task', {
        body: { taskId: task.id }
      });

      if (error) throw error;

      toast.success(`Payment of $${task.payment_amount?.toLocaleString()} completed successfully`);

      // Refresh tasks by updating this task
      await onUpdateTask(task.id, { 
        payment_status: 'paid',
        paid_at: new Date().toISOString()
      });
    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to process payment');
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<DealTask>) => {
    try {
      await onUpdateTask(taskId, updates);
      toast.success('Task updated successfully');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleSaveEdit = async (task: DealTask) => {
    if (!editForm) return;

    try {
      // Validate inputs
      const progress = parseInt(editForm.progress_percentage) || 0;
      const paymentAmount = parseFloat(editForm.payment_amount) || 0;

      if (progress < 0 || progress > 100) {
        toast.error('Progress must be between 0 and 100');
        return;
      }

      if (paymentAmount < 0) {
        toast.error('Payment amount cannot be negative');
        return;
      }

      const updates: Partial<DealTask> = {
        status: editForm.status as any,
        progress_percentage: Math.min(100, Math.max(0, progress)),
        payment_amount: Math.round(paymentAmount * 100) / 100, // Round to 2 decimals
      };

      // Auto-set payment_status to 'due' when completed with amount > 0
      if (
        editForm.status === 'completed' && 
        paymentAmount > 0 && 
        task.payment_status !== 'paid'
      ) {
        updates.payment_status = 'due';
      }

      // Also handle case where task was already completed and amount is being set/changed
      if (
        task.status === 'completed' &&
        paymentAmount > 0 &&
        task.payment_status !== 'paid'
      ) {
        updates.payment_status = 'due';
      }

      await onUpdateTask(task.id, updates);
      setEditingTaskId(null);
      setEditForm(null);
      toast.success('Task updated successfully');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleStartEdit = (task: DealTask) => {
    setEditingTaskId(task.id);
    setEditForm({
      status: task.status,
      progress_percentage: String(task.progress_percentage ?? 0),
      payment_amount: task.payment_amount != null ? String(task.payment_amount) : '0',
    });
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditForm(null);
  };

  const handleDeleteTask = async (taskId: string) => {
    if (confirm('Are you sure you want to delete this task?')) {
      try {
        await onDeleteTask(taskId);
        toast.success('Task deleted successfully');
      } catch (error) {
        toast.error('Failed to delete task');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'in_progress':
        return 'bg-primary/10 text-primary';
      case 'pending':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Deal Milestones & Tasks</h3>
        <Button
          onClick={() => setIsCreating(true)}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Task
        </Button>
      </div>

      {isCreating && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Create New Task</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Enter task title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Enter task description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-start-date">Start Date (Optional)</Label>
                <Input
                  id="task-start-date"
                  type="date"
                  value={newTask.start_date}
                  onChange={(e) => setNewTask({ ...newTask, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-due-date">Due Date</Label>
                <Input
                  id="task-due-date"
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-assigned-to">Assigned To</Label>
              <Select
                value={newTask.assigned_to}
                onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="brand">Brand</SelectItem>
                  <SelectItem value="talent">Talent</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-payment">Payment Amount ($)</Label>
              <Input
                id="task-payment"
                type="number"
                min="0"
                step="0.01"
                value={newTask.payment_amount || ''}
                onChange={(e) => setNewTask({ ...newTask, payment_amount: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
              <p className="text-xs text-muted-foreground">
                Optional: Set payment amount for task completion
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateTask}>Create Task</Button>
              <Button variant="outline" onClick={() => setIsCreating(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {tasks.map((task) => (
          <Card key={task.id} className="transition-shadow hover:shadow-md">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h4 className="font-medium">{task.title}</h4>
                    <Badge className={getStatusColor(task.status)}>
                      {task.status.replace('_', ' ')}
                    </Badge>
                    
                    {/* Payment amount badge - always show */}
                    {task.payment_amount && task.payment_amount > 0 ? (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        ${task.payment_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        No amount set
                      </Badge>
                    )}
                    
                    {/* Payment status badges - only show paid or due */}
                    {task.payment_status === 'paid' && (
                      <Badge className="bg-green-600 text-white">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Paid
                      </Badge>
                    )}
                    {task.payment_status === 'due' && (
                      <Badge className="bg-orange-600 text-white">
                        <Clock className="h-3 w-3 mr-1" />
                        Payment Due
                      </Badge>
                    )}
                    
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {task.progress_percentage}%
                    </div>
                  </div>
                  
                  <Progress value={task.progress_percentage} className="h-2" />
                  
                  {task.description && (
                    <p className="text-sm text-muted-foreground">{task.description}</p>
                  )}
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                    {task.due_date && (
                      <span>Due: {formatDate(task.due_date)}</span>
                    )}
                    <span>Assigned to: {task.assigned_to}</span>
                    {task.paid_at && (
                      <span className="text-green-600">
                        Paid: {formatDate(task.paid_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStartEdit(task)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteTask(task.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  {task.status !== 'completed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updates: Partial<DealTask> = { 
                          status: 'completed', 
                          progress_percentage: 100
                        };
                        // Only set payment_status if there's a payment amount
                        if (task.payment_amount && task.payment_amount > 0) {
                          updates.payment_status = 'due';
                        }
                        handleUpdateTask(task.id, updates);
                      }}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  )}
                  
                  {task.status === 'completed' && 
                   task.payment_status === 'due' && 
                   task.payment_amount && 
                   task.payment_amount > 0 && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handlePayTask(task)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <DollarSign className="h-4 w-4 mr-1" />
                      Pay ${task.payment_amount.toLocaleString()}
                    </Button>
                  )}
                </div>
              </div>
              
              {editingTaskId === task.id && editForm && (
                <div className="mt-4 p-4 border rounded-lg bg-muted/20 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm">Status</Label>
                      <Select
                        value={editForm.status}
                        onValueChange={(value) => setEditForm(f => ({ ...f!, status: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Progress %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editForm.progress_percentage}
                        onChange={(e) => setEditForm(f => ({ ...f!, progress_percentage: e.target.value }))}
                        placeholder="Progress %"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Payment Amount</Label>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.payment_amount}
                        onChange={(e) => setEditForm(f => ({ ...f!, payment_amount: e.target.value }))}
                        placeholder="0.00"
                        className="max-w-[200px]"
                      />
                      <span className="text-sm text-muted-foreground">USD</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Payment Status: {task.payment_status || 'unpaid'}
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleSaveEdit(task)}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {tasks.length === 0 && !isCreating && (
        <div className="text-center py-8 text-muted-foreground">
          <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No tasks created yet</p>
          <p className="text-sm">Add milestones and tasks to track deal progress</p>
        </div>
      )}
    </div>
  );
};